import {
  type CredentialResponse,
  GoogleLogin as GoogleLoginComponent,
} from '@react-oauth/google';
import { useLayoutEffect, useRef, useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useExternalLoginMutation } from '@/modules/auth/mutations/useExternalLoginMutation';
import {
  authButtonClickedEvents,
  type AuthIntent,
  authProviders,
} from '@/modules/auth/types/externalLogin';

interface GoogleLoginProps {
  onSuccess: () => void;
  onError: (error?: Error) => void;
  trialOfferCode?: string;
  /** True once the Terms Notice beside the button shows its link. */
  acceptTerms: boolean;
  /** Picks `signUpButtonClicked` or `signInButtonClicked` on click. */
  intent: AuthIntent;
  /** Google's button label: "Continue with Google" or "Sign up with Google". */
  text?: 'continue_with' | 'signup_with';
}

/** Width the AuthLayout card content has on desktop; also the fallback before measuring. */
const DEFAULT_WIDTH = 360;

export const GoogleLogin = ({
  onSuccess,
  onError,
  trialOfferCode,
  acceptTerms,
  intent,
  text = 'continue_with',
}: GoogleLoginProps) => {
  const externalLoginMutation = useExternalLoginMutation();
  const trackEvent = useTrackEvent();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  // Google's button needs a pixel width or it shrinks, so measure the
  // container and follow it on resize.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => setWidth(Math.round(container.clientWidth));
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      onError();
      return;
    }

    externalLoginMutation.mutate(
      {
        provider: authProviders.google,
        idToken: credentialResponse.credential,
        trialOfferCode,
        acceptTerms,
      },
      {
        onSuccess,
        onError,
      },
    );
  };

  return (
    <div ref={containerRef} className="w-full">
      <GoogleLoginComponent
        width={width}
        size="large"
        text={text}
        theme="outline"
        shape="rectangular"
        useOneTap
        // The button renders in Google's iframe, so a DOM onClick never sees
        // the click; GSI reports it here instead. One Tap doesn't call this.
        click_listener={() =>
          trackEvent(authButtonClickedEvents[intent], { authMethod: 'Google' })
        }
        onSuccess={handleSubmit}
        onError={onError}
      />
    </div>
  );
};
